import { DEFAULT_VOICE, type PublicConfig, type VoiceProfile } from "@mantra/shared";
import { useLiveQuery } from "dexie-react-hooks";
import { useEffect } from "react";
import { getConfig } from "./api";
import { kvGet } from "./db";

export interface VoiceOption {
  id: string;
  name: string;
  gender: "f" | "m";
  personal?: boolean;
  /** голос ещё загружается на сервере — кнопка видна, но неактивна */
  loading?: boolean;
}

export const MY_VOICE_NAME = "Мой голос";
const LOADING_POLL_MS = 8_000;

/** Голоса для выбора: с сервера (кэш в IndexedDB) + личный голос, если записан */
export function useVoices(): VoiceOption[] {
  // null — ещё читаем из IndexedDB (не путать с «конфига нет»)
  const config = useLiveQuery(async () => (await kvGet<PublicConfig>("config")) ?? false, [], null);
  const profile = useLiveQuery(async () => (await kvGet<VoiceProfile | null>("voiceProfile")) ?? false, [], null);
  const anyLoading = !!config && config.voices?.some((v) => v.status === "loading");

  // пока какие-то голоса грузятся, спрашиваем сервер чаще — кнопки включатся сами
  useEffect(() => {
    if (!anyLoading) return;
    const t = setInterval(() => document.visibilityState === "visible" && void getConfig(), LOADING_POLL_MS);
    return () => clearInterval(t);
  }, [anyLoading]);

  if (config === null || profile === null) return [];
  const base: VoiceOption[] = config && config.voices?.length
    ? config.voices.map((v) => ({ id: v.id, name: v.name, gender: v.gender, loading: v.status === "loading" }))
    : [{ id: DEFAULT_VOICE, name: "Ирина", gender: "f" }];
  return profile ? [{ id: profile.voiceId, name: MY_VOICE_NAME, gender: "f", personal: true }, ...base] : base;
}

export const voiceOptions = (voices: VoiceOption[]) =>
  voices.map((v) => ({ value: v.id, label: v.name, disabled: v.loading, hint: v.loading ? "Голос загружается на сервере, пара минут" : undefined }));

export const LOADING_NOTE = "Бледные голоса ещё загружаются на сервере и включатся сами через пару минут.";

/** Голос по умолчанию: выбранный в настройках, иначе первый подходящий по роду обращения */
export async function pickVoice(preferred: string | undefined, gender: "f" | "m" | "n"): Promise<string> {
  const cfg = await kvGet<PublicConfig>("config");
  const all = cfg?.voices ?? [];
  const list = all.filter((v) => v.status !== "loading");
  const mine = await kvGet<{ voiceId: string } | null>("voiceProfile");
  if (preferred && (all.some((v) => v.id === preferred) || mine?.voiceId === preferred)) return preferred;
  if (gender === "m") return list.find((v) => v.gender === "m")?.id ?? list[0]?.id ?? DEFAULT_VOICE;
  return list.find((v) => v.gender === "f")?.id ?? list[0]?.id ?? DEFAULT_VOICE;
}

